/**
 * TimelineReserva — Historial cronológico de eventos de una reserva.
 * Carga GET /turismo/reservaciones/{id}/timeline y renderiza una línea vertical
 * con iconos por tipo de evento, fechas relativas y descripción.
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, CircularProgress, Stack } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import PersonIcon from '@mui/icons-material/Person';
import PaymentsIcon from '@mui/icons-material/Payments';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HistoryIcon from '@mui/icons-material/History';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { API_BASE, getAuthHeaders } from './shared/format';

const ICONO_MAP = {
  create: { icon: <AddCircleIcon />, color: '#0d9488', bg: '#ccfbf1' },
  person: { icon: <PersonIcon />, color: '#0ea5e9', bg: '#e0f2fe' },
  cash: { icon: <PaymentsIcon />, color: '#16a34a', bg: '#dcfce7' },
  link: { icon: <LinkIcon />, color: '#8b5cf6', bg: '#ede9fe' },
  check: { icon: <CheckCircleIcon />, color: '#16a34a', bg: '#dcfce7' },
  swap: { icon: <SwapHorizIcon />, color: '#f59e0b', bg: '#fef3c7' },
  cancel: { icon: <CancelIcon />, color: '#dc2626', bg: '#fee2e2' },
  edit: { icon: <EditIcon />, color: '#64748b', bg: '#f1f5f9' },
  warning: { icon: <WarningAmberIcon />, color: '#dc2626', bg: '#fee2e2' },
  boat: { icon: <DirectionsBoatIcon />, color: '#0ea5e9', bg: '#e0f2fe' },
  discount: { icon: <LocalOfferIcon />, color: '#f97316', bg: '#ffedd5' },
  credit: { icon: <AccountBalanceWalletIcon />, color: '#a855f7', bg: '#f3e8ff' },
  error: { icon: <ErrorOutlineIcon />, color: '#dc2626', bg: '#fee2e2' },
  notify: { icon: <NotificationsActiveIcon />, color: '#0ea5e9', bg: '#e0f2fe' },
  history: { icon: <HistoryIcon />, color: '#64748b', bg: '#f1f5f9' },
};

function fechaRelativa(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const ahora = new Date();
    const diffMs = ahora - d;
    const diffMin = Math.round(diffMs / 60000);
    const diffH = Math.round(diffMin / 60);
    const diffD = Math.round(diffH / 24);
    if (diffMin < 1) return 'ahora';
    if (diffMin < 60) return `hace ${diffMin} min`;
    if (diffH < 24) return `hace ${diffH} h`;
    if (diffD < 7) return `hace ${diffD} día${diffD !== 1 ? 's' : ''}`;
    return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

function fechaAbsoluta(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-CO', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
}

export default function TimelineReserva({ reservaId }) {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    fetch(`${API_BASE}/turismo/reservaciones/${reservaId}/timeline`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => {
        if (cancel) return;
        setEventos(d.eventos || []);
        setEstado(d.estado_actual);
      })
      .catch(() => { if (!cancel) setEventos([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [reservaId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} sx={{ color: '#0d9488' }} />
      </Box>
    );
  }

  if (!eventos.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <HistoryIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
        <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Sin eventos registrados aún</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
          {eventos.length} evento{eventos.length !== 1 ? 's' : ''} registrado{eventos.length !== 1 ? 's' : ''}
        </Typography>
        {estado && (
          <Chip size="small" label={`Estado actual: ${estado}`}
            sx={{ fontSize: 10, fontWeight: 700, bgcolor: '#f1f5f9' }} />
        )}
      </Box>

      <Box sx={{ position: 'relative', pl: 4 }}>
        {/* Línea vertical */}
        <Box sx={{
          position: 'absolute', left: 15, top: 8, bottom: 8,
          width: 2, bgcolor: '#e2e8f0',
        }} />

        <Stack spacing={2}>
          {eventos.map((ev, idx) => {
            const meta = ICONO_MAP[ev.icono] || ICONO_MAP.history;
            return (
              <Box key={idx} sx={{ position: 'relative' }}>
                {/* Punto + icono */}
                <Box sx={{
                  position: 'absolute', left: -28, top: 0,
                  width: 32, height: 32, borderRadius: '50%',
                  bgcolor: meta.bg, color: meta.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid #fff', boxShadow: `0 0 0 2px ${meta.bg}`,
                  '& svg': { fontSize: 16 },
                }}>
                  {meta.icon}
                </Box>

                {/* Card del evento */}
                <Box sx={{
                  pl: 1.5, py: 0.5,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                      {ev.titulo}
                    </Typography>
                    <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}
                      title={fechaAbsoluta(ev.fecha)}>
                      {fechaRelativa(ev.fecha)}
                    </Typography>
                  </Box>
                  {ev.descripcion && (
                    <Typography sx={{ fontSize: 12, color: '#475569', mt: 0.25 }}>
                      {ev.descripcion}
                    </Typography>
                  )}
                  {ev.usuario && ev.usuario !== '—' && (
                    <Typography sx={{ fontSize: 10.5, color: '#64748b', mt: 0.25, fontStyle: 'italic' }}>
                      por {ev.usuario}
                      {ev.origen && ev.origen !== 'frontend' && ` · ${ev.origen}`}
                    </Typography>
                  )}
                  {ev.detalles && Object.keys(ev.detalles).length > 0 && (ev.icono === 'swap' || ev.icono === 'discount') && (
                    <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {Object.entries(ev.detalles).slice(0, 3).map(([k, v]) => (
                        <Chip key={k} size="small" label={`${k}: ${String(v).slice(0, 30)}`}
                          sx={{ height: 18, fontSize: 9, bgcolor: '#f8fafc', color: '#64748b' }} />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}
